import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Info, 
  Route, 
  Tickets, 
  PoundSterling, 
  Languages, 
  Plane, 
  Bus, 
  Train,
  Flower,
  Sun,
  Leaf,
  Snowflake
} from "lucide-react";

// Define TravelTip interface locally
interface TravelTip {
  id: string;
  title: string;
  description: string;
  category: string;
  icon: string;
}

// Renamed fallbackTravelTips to travelTipsData as it's the primary source now
const travelTipsData: TravelTip[] = [
  {
    id: "tip1",
    title: "Entry Requirements",
    description: "Most visitors need an e-Visa which can be obtained online before travel. Check requirements based on your nationality.",
    category: "practical",
    icon: "passport"
  },
  {
    id: "tip2",
    title: "Currency",
    description: "The Turkish Lira (₺) is the official currency. Credit cards are widely accepted in cities but carry cash for rural areas.",
    category: "practical",
    icon: "lira-sign"
  },
  {
    id: "tip3",
    title: "Language",
    description: "Turkish is the official language. English is commonly spoken in tourist areas and younger people often speak some English.",
    category: "practical",
    icon: "language"
  },
  {
    id: "tip4",
    title: "Summer (June-August)",
    description: "Hot and dry with temperatures reaching 35°C in some regions. Coastal areas are busy with tourists.",
    category: "season",
    icon: "sun"
  },
  {
    id: "tip5",
    title: "Spring (March-May)",
    description: "Mild temperatures and blooming wildflowers make this an ideal time for exploring cities and hiking.",
    category: "season",
    icon: "flower"
  },
  {
    id: "tip6",
    title: "Autumn (September-November)",
    description: "Pleasant temperatures and fewer crowds. Great for sightseeing and experiencing local culture.",
    category: "season",
    icon: "leaf"
  },
  {
    id: "tip7",
    title: "Winter (December-February)",
    description: "Cold in central and eastern regions with snow perfect for skiing. Milder on the Mediterranean coast.",
    category: "season",
    icon: "snowflake"
  }
];

export default function TravelTips() {
  // Removed useQuery hook and related states (isLoading, error, apiTravelTips)
  
  // Directly use the hardcoded data
  const travelTips = travelTipsData;

  // Get icon based on icon name from the database
  const getIcon = (iconName: string) => {
    switch (iconName.toLowerCase()) {
      case 'passport': return <Tickets className="text-[#30D5C8] mt-1 mr-3" />;
      case 'lira-sign': return <PoundSterling className="text-[#30D5C8] mt-1 mr-3" />;
      case 'language': return <Languages className="text-[#30D5C8] mt-1 mr-3" />;
      case 'plane': return <Plane className="text-[#30D5C8] mt-1 mr-3" />;
      case 'bus': return <Bus className="text-[#30D5C8] mt-1 mr-3" />;
      case 'train': return <Train className="text-[#30D5C8] mt-1 mr-3" />;
      case 'flower': return <Flower className="text-[#30D5C8] mt-1 mr-3" />;
      case 'sun': return <Sun className="text-[#30D5C8] mt-1 mr-3" />;
      case 'leaf': return <Leaf className="text-[#30D5C8] mt-1 mr-3" />;
      case 'snowflake': return <Snowflake className="text-[#30D5C8] mt-1 mr-3" />;
      default: return <Info className="text-[#30D5C8] mt-1 mr-3" />;
    }
  };

  // Filter tips by category
  const practicalTips = travelTips?.filter(tip => tip.category === 'practical') || [];
  const seasonTips = travelTips?.filter(tip => tip.category === 'season') || [];

  return (
    <section className="py-12 bg-[#F5F5F5]">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-poppins font-bold text-[#333333] mb-8 text-center">Travel Tips</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-8">
          {/* Practical Info Card */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-start mb-4">
              <div className="bg-[#E30A17] rounded-full p-3 text-white mr-4">
                <Info className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-xl font-poppins font-bold text-[#333333] mb-2">Practical Information</h3>
                <p className="text-gray-600">Essential details to help you plan your trip to Turkey.</p>
              </div>
            </div>
            
            {practicalTips.length === 0 ? ( // Changed isLoading to check if practicalTips is empty
              // Skeleton loading state (kept for visual consistency if needed, or remove if hardcoded data is always present)
              <div className="space-y-4">
                {Array(3).fill(0).map((_, index) => (
                  <div key={index} className="flex items-start">
                    <Skeleton className="h-6 w-6 mr-3" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-40 mb-2" />
                      <Skeleton className="h-3 w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <ul className="space-y-4">
                {practicalTips.slice(0, 3).map((tip) => (
                  <li key={tip.id} className="flex items-start">
                    {getIcon(tip.icon)}
                    <div>
                      <h4 className="font-poppins font-semibold">{tip.title}</h4>
                      <p className="text-sm text-gray-600">{tip.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            
            <div className="mt-4">
              <Link href="/practical-info">
                <span className="inline-block text-[#30D5C8] font-poppins font-semibold hover:text-[#E30A17] transition duration-200 cursor-pointer">
                  View All Practical Info <span className="ml-1">→</span>
                </span>
              </Link>
            </div>
          </div>
          
          {/* Transportation Card */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-start mb-4">
              <div className="bg-[#E30A17] rounded-full p-3 text-white mr-4">
                <Route className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-xl font-poppins font-bold text-[#333333] mb-2">Getting Around</h3>
                <p className="text-gray-600">Tips for navigating Turkey's transportation options.</p>
              </div>
            </div>
            
            <ul className="space-y-4">
              <li className="flex items-start">
                <Plane className="text-[#30D5C8] mt-1 mr-3" />
                <div>
                  <h4 className="font-poppins font-semibold">Domestic Flights</h4>
                  <p className="text-sm text-gray-600">
                    Turkey has an extensive domestic flight network connecting major cities and tourist destinations.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <Bus className="text-[#30D5C8] mt-1 mr-3" />
                <div>
                  <h4 className="font-poppins font-semibold">Intercity Buses</h4>
                  <p className="text-sm text-gray-600">
                    Comfortable and affordable buses connect most towns and cities with frequent departures.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <Train className="text-[#30D5C8] mt-1 mr-3" />
                <div>
                  <h4 className="font-poppins font-semibold">Railways</h4>
                  <p className="text-sm text-gray-600">
                    High-speed trains operate between major cities, offering a scenic and comfortable travel option.
                  </p>
                </div>
              </li>
            </ul>
            
            <div className="mt-4">
              <Link href="/transportation">
                <span className="inline-block text-[#30D5C8] font-poppins font-semibold hover:text-[#E30A17] transition duration-200 cursor-pointer">
                  Transportation Guide <span className="ml-1">→</span>
                </span>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Best Time to Visit */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-poppins font-bold text-[#333333] mb-4">Best Time to Visit</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {seasonTips.length === 0 ? ( // Changed isLoading to check if seasonTips is empty
              // Skeleton loading state (kept for visual consistency if needed, or remove if hardcoded data is always present)
              Array(4).fill(0).map((_, index) => (
                <div key={index} className="border border-[#E0E0E0] rounded-lg p-4">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <div className="flex items-center justify-between mt-2">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))
            ) : (
              // Season cards
              seasonTips.map((season) => ( // Removed length check as we assume data exists
                <div key={season.id} className="border border-[#E0E0E0] rounded-lg p-4">
                  <div className="flex items-center mb-2"> {/* Added flex container for icon and title */}
                    {getIcon(season.icon)}
                    <h4 className="font-poppins font-semibold text-[#30D5C8]">{season.title}</h4> {/* Removed margin bottom */}
                  </div>
                  <p className="text-sm text-gray-600">{season.description}</p> {/* Removed margin bottom */}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
